import autobind from 'autobind-decorator';
import { isEqual } from 'lodash';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { H2 } from '@shared/ui-components';

import { SubscriptionType } from 'common/constants/subscription';
import { RequestStatus } from 'common/enums/request-status';
import { State } from 'common/interfaces/state';
import { KreoDialogActions } from 'common/UIKit';
import { DeferredExecutor } from 'common/utils/deferred-executer';
import { MIN_TEAM_SIZE } from 'unit-subscription/constants';
import { EstimateSubscriptionError } from 'unit-subscription/interfaces/estimate-subscription-error';
import { CompanySubscriptionModel } from '../../../../account/interfaces/company-subscription-model';
import { AccountSelectors } from '../../../../account/selectors';
import { SubscriptionActions } from '../../../actions/creators';
import { BillingPeriodUnit } from '../../../enums/billing-period-unit';
import { CompanyBillingInfoModel } from '../../../interfaces/company-billing-info-model';
import { EstimateSubscriptionForm } from '../../../interfaces/estimate-subscription-form';
import { EstimateSubscriptionModel } from '../../../interfaces/estimation-subscription-model';
import { PaymentSourcesModel } from '../../../interfaces/payment-sources-model';
import { SubscriptionDetailsModel } from '../../../interfaces/subscription-details';
import { SubscriptionPlanListingModel } from '../../../interfaces/subscription-plan-listing-model';
import { UpdateSubscriptionForm } from '../../../interfaces/update-subscription-form';
import { SubscriptionListingUtils } from '../../../utils/subscription-listing-utils';
import { SubscriptionDetails } from '../../subscription-details';
import { SELECT_SUBSCRIPTION_PLAN__DIALOG, SelectSubscriptionPlanDialog } from '../select-subscription-plan-dialog';
import { CANT_SELECT_PLAN_DIALOG, IndividualPlanDialogInfo } from './individual-plan-dialog-info';
import { Styled } from './styled';


const UPDATE_ESTIMATION_DELAY = 1000;
const UPDATE_PAYMENT_METHOD_DELAY = 2000;

interface DispatchProps {
  getSubscriptionPlans: (subscriptionType: SubscriptionType) => void;
  setSubscriptionPaymentMethod: (subscriptionId: string, paymentMethodId: string) => void;
  getOwnCompanyBillingInfo: (subscriptionType: SubscriptionType) => void;
  updateSubscription: (subscriptionId: string, form: UpdateSubscriptionForm) => void;
  renewSubscription: (subscriptionId: string, form: UpdateSubscriptionForm) => void;
  getSubscriptionEstimation: (subscriptionId: string, form: EstimateSubscriptionForm) => void;
  cancelSubscription: (subscriptionId: string) => void;
  dropSubscriptionPlansState: () => void;
  dropOwnCompanyBillingInfoState: () => void;
  openDialog: (dialogName: string) => void;
}

interface StateProps {
  billingEstimationError: EstimateSubscriptionError;
  subscriptionType: SubscriptionType;
  subscription: CompanySubscriptionModel;
  ownCompanyBillingInfo: CompanyBillingInfoModel;
  subscriptionPlansRequestStatus: RequestStatus;
  ownCompanyBillingInfoRequestStatus: RequestStatus;
  plansModel: SubscriptionPlanListingModel | null;
  paymentSource: PaymentSourcesModel;
  billingEstimation: EstimateSubscriptionModel;
  paidEmployeesCount: number;
  guestUsersCount: number;
}

type SubscriptionEditorMode = 'editor' | 'renew' | 'seats manage';

interface Props extends DispatchProps, StateProps {
  mode?: SubscriptionEditorMode;
  onSubscriptionUpdate?: () => void;
  onSubscriptionCancel?: () => void;
}

interface OwnState {
  selectedPlanGroup: string;
  paidPlanGroup: string;
  billingPeriod: BillingPeriodUnit;
  selectedAddonGroups: string[];
  paidAddonGroups: string[];
  paymentCardId: string;
  teamSize: number;
  coupons: string[];
  billingCountry: string;
  vatNumber: string;
}

class SubscriptionEditorComponent extends React.Component<Props, OwnState> {
  private getEstimationDeferredExecutor: DeferredExecutor = new DeferredExecutor(UPDATE_ESTIMATION_DELAY);
  private setPaymentMethodDeferredExecutor: DeferredExecutor = new DeferredExecutor(UPDATE_PAYMENT_METHOD_DELAY);

  constructor(props: Props) {
    super(props);
    this.state = {
      teamSize: 2,
      selectedPlanGroup: null,
      paidPlanGroup: null,
      billingPeriod: null,
      paymentCardId: null,
      selectedAddonGroups: [],
      paidAddonGroups: [],
      coupons: [],
      billingCountry: 'GB',
      vatNumber: null,
    };
  }

  public componentDidMount(): void {
    this.fetchSubscriptionInfoIfNeeded();
  }

  public componentDidUpdate(prevProps: Props): void {
    this.fetchSubscriptionInfoIfNeeded();
    this.setInitialStateIfAllDataIsReady(prevProps);
  }

  public render(): JSX.Element {
    const { plansModel, ownCompanyBillingInfo } = this.props;
    const { billingPeriod, selectedAddonGroups } = this.state;

    if (!ownCompanyBillingInfo || !plansModel) {
      return null;
    }

    const filteredPlanGroups = SubscriptionListingUtils.getFilteredPlanGroups(plansModel, billingPeriod);
    const filteredAddons = SubscriptionListingUtils.getFilteredAddons(plansModel, billingPeriod);
    const selectedAddons = SubscriptionListingUtils.getSelectedAddons(plansModel, billingPeriod, selectedAddonGroups);
    const planVariant = filteredPlanGroups.find(x => x.name === this.state.selectedPlanGroup) || filteredPlanGroups[0];
    // -- temp hack for sketchup promo subscription
    const isPromo = planVariant?.id && planVariant?.id.includes('sketchup');
    // --
    const minTeamSize = planVariant?.minSeatCount || MIN_TEAM_SIZE;
    const currentTeamSize = this.state.teamSize < minTeamSize ? minTeamSize : this.state.teamSize;

    if (!planVariant) {
      return null;
    }

    if (ownCompanyBillingInfo.isMocked) {
      return (
        <Styled.MockPlanContainer>
          <H2>You have no rights to manage subscription</H2>
        </Styled.MockPlanContainer>
      );
    }

    const canUpdateSubscription = this.canUpdateSubscription();

    const applyButtonText = this.props.mode !== 'renew'
      ? 'Update Plan'
      : 'Renew';

    const cancelSubscriptionClick = this.props.mode === 'editor'
      ? this.onCancelSubscription
      : null;

    const header = isPromo && !this.state.paymentCardId
      ? 'Enter your payment details'
      : 'Review your plan and upgrade';

    return (
      <>
        <SubscriptionDetails
          addons={filteredAddons}
          applyButtonCaption={applyButtonText}
          estimationError={this.props.billingEstimationError}
          currentSubscription={{
            planVariant,
            teamSize: this.state.teamSize,
            selectedAddonIds: selectedAddons.map(x => x.id),
            billingPeriod: this.state.billingPeriod,
            coupons: this.state.coupons,
          }}
          header={header}
          currentTeamSize={currentTeamSize}
          minTeamSize={minTeamSize}
          onApplyClick={canUpdateSubscription ? this.onApplyClick : null}
          onCancelSubscriptionClick={cancelSubscriptionClick}
          onChangePlanClick={!isPromo ? this.openSelectSubscriptionPlanDialog : null}
          onSubscriptionChange={!isPromo ? this.onSubscriptionChange: null}
          onChangePaymentCardId={this.onChangePaymentCardId}
          onCountryIdChanged={this.onCountryIdChanged}
          onVatNumberIdChanged={this.onVatNumberIdChanged}
          paymentCardId={this.state.paymentCardId}
          countryId={this.state.billingCountry}
          vatNumber={this.state.vatNumber}
          billingEstimation={this.props.billingEstimation}
        />
        <SelectSubscriptionPlanDialog
          billingPeriod={this.state.billingPeriod}
          filteredAddons={filteredAddons}
          filteredPlanGroups={filteredPlanGroups}
          selectedAddonGroups={selectedAddonGroups}
          selectedAddons={selectedAddons}
          selectedPlanGroup={this.state.selectedPlanGroup}
          onBillingPeriodChanged={this.onBillingPeriodChanged}
          onSelectedAddonGroupsChanged={this.onSelectedAddonGroupsChanged}
          onSubscriptionPlanGroupClick={this.setSubscriptionPlanGroup}
        />
        <IndividualPlanDialogInfo />
      </>
    );
  }

  public componentWillUnmount(): void {
    this.props.dropOwnCompanyBillingInfoState();
    this.props.dropSubscriptionPlansState();
    this.getEstimationDeferredExecutor.reset();
  }

  @autobind
  private onCountryIdChanged(billingCountry: string): void {
    this.setState({ billingCountry });
    this.getEstimationIfNeeded();
  }

  @autobind
  private onVatNumberIdChanged(vatNumber: string): void {
    this.setState({ vatNumber });
    this.getEstimationIfNeeded();
  }

  @autobind
  private onChangePaymentCardId(paymentCardId: string): void {
    this.setState({ paymentCardId });
    const { ownCompanyBillingInfo } = this.props;
    this.setPaymentMethodDeferredExecutor.execute(() => {
      this.props.setSubscriptionPaymentMethod(ownCompanyBillingInfo.subscriptionId, paymentCardId);
    });
    this.getEstimationIfNeeded();
  }

  private fetchSubscriptionInfoIfNeeded(): void {
    if (this.props.subscriptionPlansRequestStatus === RequestStatus.NotRequested) {
      this.props.getSubscriptionPlans(this.props.subscriptionType);
    }

    if (this.props.ownCompanyBillingInfoRequestStatus === RequestStatus.NotRequested) {
      this.props.getOwnCompanyBillingInfo(this.props.subscriptionType);
    }
  }

  private getPlanGroupNameByPlanId(id: string): string {
    const { plansModel } = this.props;
    const group = plansModel && plansModel.plans.find(x => x.variants.find(v => v.id === id));

    return group && group.name;
  }

  private getAddonGroupsByAddonIds(addonIds: string[]): string[] {
    const { plansModel } = this.props;
    return plansModel && plansModel.addons.filter(x => addonIds.includes(x.id)).map(x => x.groupName);
  }

  private isSubscriptionChanged(): boolean {
    const state = this.state;
    const currentPlan = this.props.ownCompanyBillingInfo;
    const currentPayment = this.props.paymentSource;

    if (!currentPlan || !currentPayment || currentPlan.isMocked) {
      return false;
    }

    return state.billingPeriod !== currentPlan.billingPeriodUnit
      || state.billingCountry !== (currentPayment && currentPayment.billingCountry)
      || state.vatNumber !== (currentPayment && currentPayment.vatNumber)
      || state.teamSize !== currentPlan.licensesCount
      || state.paidPlanGroup !== state.selectedPlanGroup
      || !!state.coupons.length
      || this.isAddonsChanged();
  }

  private canUpdateSubscription(): boolean {
    return (this.isSubscriptionChanged() || this.props.mode === 'renew')
      && !!this.state.paymentCardId
      && !!this.state.billingCountry
      && !this.props.billingEstimationError;
  }

  private isAddonsChanged(): boolean {
    const { paidAddonGroups, selectedAddonGroups } = this.state;
    const paid = new Set(paidAddonGroups);
    const selected = new Set(selectedAddonGroups);

    if (paid.size !== selected.size) {
      return true;
    }
    for (const group of paid) {
      if (!selected.has(group)) {
        return true;
      }
    }

    return false;
  }

  @autobind
  private onSubscriptionChange(model: SubscriptionDetailsModel): void {
    if (this.state.billingPeriod !== model.billingPeriod) {
      this.onBillingPeriodChanged(model.billingPeriod);
    }

    const selectedGroupNames = this.props.plansModel.addons
      .filter(x => model.selectedAddonIds.includes(x.id))
      .map(x => x.groupName);
    if (!isEqual(this.state.selectedAddonGroups, selectedGroupNames)) {
      this.onSelectedAddonGroupsChanged(selectedGroupNames);
    }

    if (this.state.teamSize !== model.teamSize) {
      this.setState({ teamSize: model.teamSize });
    }

    if (this.state.coupons !== model.coupons) {
      this.setState({ coupons: model.coupons });
    }

    this.getEstimationIfNeeded();
  }

  @autobind
  private onBillingPeriodChanged(billingPeriod: BillingPeriodUnit): void {
    this.setState({ billingPeriod });
    this.getEstimationIfNeeded();
  }

  @autobind
  private onSelectedAddonGroupsChanged(selectedAddonGroups: string[]): void {
    this.setState({ selectedAddonGroups });
    this.getEstimationIfNeeded();
  }

  @autobind
  private setSubscriptionPlanGroup(selectedPlanGroup: string): void {
    const { billingPeriod } = this.state;
    const { plansModel, paidEmployeesCount, guestUsersCount } = this.props;
    const filteredPlanGroups = SubscriptionListingUtils.getFilteredPlanGroups(plansModel, billingPeriod);
    const planVariant = filteredPlanGroups.find(x => x.name === selectedPlanGroup);

    if (planVariant.isCollaborationBlocked && (paidEmployeesCount > 1 || guestUsersCount)) {
      this.props.openDialog(CANT_SELECT_PLAN_DIALOG);
      return;
    }

    this.setState({ selectedPlanGroup });
  }

  @autobind
  private onCancelSubscription(): void {
    const subscriptionId = this.props.ownCompanyBillingInfo.subscriptionId;
    const { cancelSubscription, onSubscriptionCancel } = this.props;
    cancelSubscription(subscriptionId);

    if (onSubscriptionCancel) {
      onSubscriptionCancel();
    }
  }

  @autobind
  private openSelectSubscriptionPlanDialog(): void {
    this.props.openDialog(SELECT_SUBSCRIPTION_PLAN__DIALOG);
  }

  private getEstimationIfNeeded(): void {
    this.getEstimationDeferredExecutor.execute(() => {
      if (!this.props.ownCompanyBillingInfo.isMocked) {
        const { ownCompanyBillingInfo } = this.props;
        const form = this.getForm() as EstimateSubscriptionForm;
        this.props.getSubscriptionEstimation(ownCompanyBillingInfo.subscriptionId, form);
      }
    });
  }

  private getAlternativePlanGroupNameIfCurrentIsNotAvailable(): string {
    const { plansModel } = this.props;
    return SubscriptionListingUtils.getFilteredPlanGroups(plansModel, this.getBillingUnit())
      .find(x => x.isSelfService)
      .name;
  }

  private getBillingUnit(billingUnit?: BillingPeriodUnit): BillingPeriodUnit {
    if ([BillingPeriodUnit.Month, BillingPeriodUnit.Year].includes(billingUnit)) {
      return billingUnit;
    }

    return BillingPeriodUnit.Month;
  }

  private setInitialStateIfAllDataIsReady(prevProps: Props): void {
    const { plansModel, ownCompanyBillingInfo, paymentSource, paidEmployeesCount } = this.props;
    if (plansModel && ownCompanyBillingInfo && (
      plansModel !== prevProps.plansModel
      || ownCompanyBillingInfo !== prevProps.ownCompanyBillingInfo
    )) {
      const paidPlanGroup = this.getPlanGroupNameByPlanId(ownCompanyBillingInfo.planId);
      const alternativePlantGroupName = this.getAlternativePlanGroupNameIfCurrentIsNotAvailable();
      const paidAddonGroups = this.getAddonGroupsByAddonIds(ownCompanyBillingInfo.addons);

      this.setState({
        teamSize: ownCompanyBillingInfo.licensesCount < paidEmployeesCount
          ? paidEmployeesCount
          : ownCompanyBillingInfo.licensesCount,
        selectedPlanGroup: paidPlanGroup || alternativePlantGroupName,
        paidPlanGroup,
        billingPeriod: this.getBillingUnit(ownCompanyBillingInfo.billingPeriodUnit),
        paymentCardId: ownCompanyBillingInfo.paymentSourceId,
        selectedAddonGroups: paidAddonGroups,
        paidAddonGroups,
      });

      this.getEstimationIfNeeded();
    }

    if (paymentSource && paymentSource !== prevProps.paymentSource) {
      this.setState({
        paymentCardId: this.state.paymentCardId || paymentSource.primaryCardId,
        billingCountry: paymentSource.billingCountry,
        vatNumber: paymentSource.vatNumber,
      });
    }
  }

  private getForm(): UpdateSubscriptionForm | EstimateSubscriptionForm {
    const { plansModel } = this.props;
    const {
      teamSize,
      selectedAddonGroups,
      selectedPlanGroup,
      billingPeriod,
      vatNumber,
      billingCountry,
      coupons,
    } = this.state;
    const selectedAddons = SubscriptionListingUtils.getSelectedAddons(plansModel, billingPeriod, selectedAddonGroups);
    const addons = selectedAddons.map(x => x.id);
    const planVariant = SubscriptionListingUtils.getSelectedPlanVariant(plansModel, billingPeriod, selectedPlanGroup);
    const planId = planVariant.id;
    const quantity = planVariant.isCollaborationBlocked ? 1 : teamSize;
    const reactivate = this.props.mode === 'renew';

    return { addons, quantity, planId, vatNumber, coupons, billingCountry, reactivate };
  }

  @autobind
  private onApplyClick(): void {
    const { ownCompanyBillingInfo } = this.props;

    if (this.props.mode === 'renew') {
      const form = this.getForm();
      this.props.renewSubscription(ownCompanyBillingInfo.subscriptionId, form);
    } else if (this.isSubscriptionChanged()) {
      const form = this.getForm();
      this.props.updateSubscription(ownCompanyBillingInfo.subscriptionId, form);
    }

    if (this.props.onSubscriptionUpdate) {
      this.props.onSubscriptionUpdate();
    }
  }
}


function mapStateToProps(state: State): StateProps {
  return {
    subscriptionType: state.account.selectedSubscriptionType,
    subscriptionPlansRequestStatus: state.account.requests.subscriptionPlans,
    ownCompanyBillingInfoRequestStatus: state.account.requests.ownCompanyBillingInfo,
    plansModel: state.account.subscriptionPlans,
    ownCompanyBillingInfo: state.account.ownCompanyBillingInfo,
    subscription: AccountSelectors.currentSubscription(state),
    paymentSource: state.account.payment,
    billingEstimation: state.account.billingEstimation,
    paidEmployeesCount: AccountSelectors.getPaidEmployeesCount(state),
    guestUsersCount: AccountSelectors.getGuestUsersCount(state),
    billingEstimationError: state.account.billingEstimationError,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    getSubscriptionPlans: type => dispatch(SubscriptionActions.getSubscriptionPlansRequest(type)),
    setSubscriptionPaymentMethod: (subscriptionId, paymentMethodId) =>
      dispatch(SubscriptionActions.setSubscriptionPaymentMethod(subscriptionId, paymentMethodId)),
    dropSubscriptionPlansState: () => dispatch(SubscriptionActions.dropSubscriptionPlansState()),
    getOwnCompanyBillingInfo: type => dispatch(SubscriptionActions.fetchOwnCompanyBillingInfo(type)),
    dropOwnCompanyBillingInfoState: () => dispatch(SubscriptionActions.dropOwnCompanyBillingInfoState()),
    openDialog: (dialogName) => dispatch(KreoDialogActions.openDialog(dialogName)),
    cancelSubscription: subscriptionId => dispatch(SubscriptionActions.cancelSubscription(subscriptionId)),
    updateSubscription: (subscriptionId, form) =>
      dispatch(SubscriptionActions.updateSubscription(subscriptionId, form)),
    getSubscriptionEstimation: (subscriptionId, form) =>
      dispatch(SubscriptionActions.getUpdateSubscriptionEstimation(subscriptionId, form)),
    renewSubscription: (subscriptionId, form) => dispatch(SubscriptionActions.renewSubscription(subscriptionId, form)),
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);
export const SubscriptionEditor = connector(SubscriptionEditorComponent);
